import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ErrorResponse } from 'legacy/common/utils/error';
import {
  IncomingAssociationFormCreationDto,
  IncomingAssociationFormUpdateDto,
  OutgoingAssociationFormDto,
  OutgoingAssociationFormsDto,
  IncomingFormResponseDto,
  OutgoingFormResponseDto
} from 'api/dtos/workflows/forms';
import {
  createAssociationForm,
  getAssociationForms,
  getAssociationFormById,
  updateAssociationForm,
  createFormResponse,
  getFormResponse,
  updateFormResponse,
  uploadFiles,
  deleteFile
} from 'api/services/workflows/forms';

// Query keys
export const associationFormsKeys = {
  all: ['associationForms'] as const,
  lists: (associationId: string) =>
    ['associationForms', { associationId }] as const,
  detail: (associationId: string, formId: string) =>
    ['associationForms', { associationId, formId }] as const,
  responses: (associationId: string, formId: string) =>
    ['associationFormResponses', { associationId, formId }] as const,
  responseDetail: (associationId: string, formId: string, responseId: string) =>
    ['associationFormResponses', { associationId, formId, responseId }] as const
};

// Queries
export function useGetAssociationForms(
  { associationId }: { associationId: string },
  options?: { enabled?: boolean }
) {
  return useQuery<OutgoingAssociationFormsDto, ErrorResponse>(
    associationFormsKeys.lists(associationId),
    () => getAssociationForms(associationId),
    {
      enabled: associationId ? options?.enabled ?? true : false
    }
  );
}

export function useGetAssociationFormById({
  associationId,
  formId
}: {
  associationId: string;
  formId: string;
}) {
  return useQuery<OutgoingAssociationFormDto, ErrorResponse>(
    associationFormsKeys.detail(associationId, formId),
    () => getAssociationFormById({ associationId, formId }),
    {
      enabled: !!associationId && !!formId
    }
  );
}

export function useGetFormResponse({
  associationId,
  formId,
  responseId
}: {
  associationId: string;
  formId: string;
  responseId: string;
}) {
  return useQuery<OutgoingFormResponseDto, ErrorResponse>(
    associationFormsKeys.responseDetail(associationId, formId, responseId),
    () => getFormResponse({ associationId, formId, responseId })
  );
}

// Mutations
export function useCreateAssociationForm() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingAssociationFormDto,
    ErrorResponse,
    {
      associationId: string;
      form: IncomingAssociationFormCreationDto;
    }
  >(createAssociationForm, {
    onSuccess: (_, { associationId }) => {
      queryClient.invalidateQueries(associationFormsKeys.lists(associationId));
    }
  });
}

export function useUpdateAssociationForm() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingAssociationFormDto,
    ErrorResponse,
    {
      associationId: string;
      formId: string;
      form: IncomingAssociationFormUpdateDto;
    }
  >(updateAssociationForm, {
    onSuccess: (_, { associationId, formId }) => {
      queryClient.invalidateQueries(associationFormsKeys.lists(associationId));
      queryClient.invalidateQueries(
        associationFormsKeys.detail(associationId, formId)
      );
    }
  });
}

export function useCreateFormResponse() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingFormResponseDto,
    ErrorResponse,
    {
      associationId: string;
      formId: string;
      response: IncomingFormResponseDto;
    }
  >(createFormResponse, {
    onSuccess: (_, { associationId, formId }) => {
      queryClient.invalidateQueries(
        associationFormsKeys.responses(associationId, formId)
      );
    }
  });
}

export function useUpdateFormResponse() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingFormResponseDto,
    ErrorResponse,
    {
      associationId: string;
      formId: string;
      responseId: string;
      response: IncomingFormResponseDto;
    }
  >(updateFormResponse, {
    onSuccess: (_, { associationId, formId, responseId }) => {
      queryClient.invalidateQueries(
        associationFormsKeys.responses(associationId, formId)
      );
      queryClient.invalidateQueries(
        associationFormsKeys.responseDetail(associationId, formId, responseId)
      );
    }
  });
}

export function useUploadFiles() {
  return uploadFiles;
}

export function useDeleteFile() {
  return deleteFile;
}
